/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useContentStackBlogMenuNavigationData } from '../api/getContentStackBlogMenuNavigationData';
import { CMSLink } from '@/features/contentstack/components/CMSLink/CMSLink';
import styles from './BlogMenuNavigation.module.scss';
import { View, Text } from '@az/starc-ui';
import NextImage from '@/components/NextImage';
import MenuLinkGrid, { type MenuLink } from './MenuLinkGrid/MenuLinkGrid';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { MobileBlogMenuNavigation } from './MobileBlogMenuNavigation/MobileBlogMenuNavigation';
import { useLabels } from '@/hooks/useLabels';
import root from 'window-or-global';

const labelMap = {
  featuredSectionHeading: 'label_BLOG_FeaturedSectionHeading',
};

const BlogMenuNavigation: React.FC = () => {
  const labels = useLabels(labelMap);
  const { data } = useContentStackBlogMenuNavigationData();
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [menuTopPosition, setMenuTopPosition] = useState(0);
  const [menuLeftPosition, setMenuLeftPosition] = useState(0);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery((theme) => theme.breakpoints.only('md'));
  const menuRef = useRef<HTMLDivElement>(null);
  const menuContentRef = useRef<HTMLDivElement>(null);
  const linkGrid = useCallback((links: MenuLink[] = []) => {
    const grid = [];
    for (let i = 0; i < links.length; i += 8) {
      grid.push(links.slice(i, i + 8));
    }
    return grid;
  }, []);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (!activeSection || !menuRef.current || !menuContentRef.current) {
        return;
      }
      if (!(event.target instanceof Node)) {
        return;
      }
      const sectionButton = menuRef.current.querySelector(`[data-section-id="${activeSection}"]`);
      if (
        !menuContentRef.current.contains(event.target) &&
        sectionButton &&
        !sectionButton.contains(event.target)
      ) {
        setActiveSection(null);
      }
    },
    [activeSection],
  );

  const handleUpdateMenuPosition = useCallback(() => {
    if (!menuRef.current || !isTablet) {
      return;
    }
    const menuRect = menuRef.current.getBoundingClientRect();
    setMenuTopPosition(menuRect.bottom);
  }, [isTablet]);

  useEffect(() => {
    const controller = new AbortController();
    if (activeSection) {
      document.addEventListener('mousedown', handleClickOutside, { signal: controller.signal });
      document.addEventListener('click', handleClickOutside, { signal: controller.signal });
      document.addEventListener('scroll', handleUpdateMenuPosition, { signal: controller.signal });
      document.addEventListener('resize', handleUpdateMenuPosition, { signal: controller.signal });
    }
    return () => {
      controller.abort();
    };
  }, [activeSection, handleClickOutside, handleUpdateMenuPosition]);

  useEffect(() => {
    if (!activeSection || !menuContentRef.current || !isTablet) {
      return;
    }

    const menuWidth = menuContentRef.current.offsetWidth;
    const viewportWidth = root.innerWidth;
    const padding = 16;
    const maxLeft = viewportWidth - menuWidth - padding;
    const adjustedLeft = Math.min(Math.max(padding, menuLeftPosition), maxLeft);

    setMenuLeftPosition(adjustedLeft);
  }, [activeSection, isTablet, menuLeftPosition]);

  const handleToggle = useCallback(
    (sectionId: string) => {
      if (activeSection === sectionId) {
        setActiveSection(null);
      } else {
        setActiveSection(sectionId);
        handleUpdateMenuPosition();
      }
    },
    [activeSection, handleUpdateMenuPosition],
  );

  const handleLinkClick = useCallback(() => {
    setActiveSection(null);
  }, []);

  if (isMobile) {
    return <MobileBlogMenuNavigation />;
  }

  return (
    <View attributes={{ ref: menuRef }} className={`${styles.blogMenuContainer}`}>
      <View className={styles.menuHeadersRow}>
        {data?.menu_navigation?.map((section) => {
          const sectionId = section.menu_heading ?? '';
          const isOpen = activeSection === sectionId;
          const hasHeadingLink = section.menu_heading_link;
          const linkColumns =
            isOpen && !hasHeadingLink ? linkGrid(section?.menu_link_group || []) : [];
          return (
            <View
              key={sectionId}
              attributes={{
                'data-section-id': sectionId,
              }}
              className={`${styles.menuSection} ${isOpen && !hasHeadingLink ? styles.active : ''}`}
            >
              {hasHeadingLink ? (
                <View className={styles.menuHeader}>
                  <CMSLink to={section?.menu_heading_link || ''} className={styles.menuHeading}>
                    {section.menu_heading}
                  </CMSLink>
                </View>
              ) : (
                <View
                  className={styles.menuHeader}
                  attributes={{
                    onClick: (e: React.MouseEvent<HTMLDivElement>) => {
                      const left = e.currentTarget.getBoundingClientRect().left;
                      setMenuLeftPosition(left);
                      handleToggle(sectionId);
                    },
                  }}
                >
                  <Text className={styles.menuHeading}>{section.menu_heading}</Text>
                  <View
                    className={`${styles.toggleIcon} ${
                      isOpen ? styles.arrowUpPosition : styles.arrowDownPosition
                    }`}
                  />
                </View>
              )}
              {isOpen && !hasHeadingLink && (
                <View
                  attributes={{
                    style: {
                      top: isTablet ? menuTopPosition : undefined,
                      left: isTablet ? menuLeftPosition : undefined,
                    },
                    ref: menuContentRef,
                  }}
                  className={styles.menuContent}
                >
                  <View className={styles.dropdownHeader}>
                    <Text className={styles.sectionTitle}>{section.section_title}</Text>
                    {section?.section_cta?.href && (
                      <View className={styles.sectionCta}>
                        <CMSLink to={section.section_cta.href}>{section.section_cta.title}</CMSLink>
                      </View>
                    )}
                  </View>
                  <View className={styles.contentColumns}>
                    <MenuLinkGrid linkColumns={linkColumns} onLinkClick={handleLinkClick} />
                    {section.feature_link_content && section.feature_link_content.heading && (
                      <>
                        {linkColumns.length > 0 && <View className={styles.columnSeparator} />}
                        <View className={styles.featuredContentColumn}>
                          <Text className={styles.featuredText}>
                            {labels.featuredSectionHeading}
                          </Text>
                          <View className={styles.featuredContent}>
                            {section.feature_link_content.image_url && (
                              <NextImage
                                src={section.feature_link_content.image_url}
                                alt={section.feature_link_content.heading}
                                className={styles.featuredImage}
                                width={220}
                                height={135}
                              />
                            )}
                            <Text className={styles.featuredContentHeading}>
                              {section.feature_link_content.heading}
                            </Text>
                            {section.feature_link_content.sub_heading && (
                              <Text className={styles.featuredContentSubHeading}>
                                {section.feature_link_content.sub_heading}
                              </Text>
                            )}
                            {section?.feature_link_content?.cta?.href && (
                              <View className={styles.featuredCtaWrapper}>
                                <CMSLink
                                  to={section.feature_link_content.cta.href}
                                  className={styles.featuredCta}
                                >
                                  {section.feature_link_content.cta.title}
                                </CMSLink>
                                <View className={styles.arrowRightPosition} />
                              </View>
                            )}
                          </View>
                        </View>
                      </>
                    )}
                  </View>
                </View>
              )}
            </View>
          );
        })}
      </View>
    </View>
  );
};

export default BlogMenuNavigation;
